import { useState, useEffect } from "react";
import { Link, usePage, router } from "@inertiajs/react";
import {
  Users,
  Tags,
  MessageSquare,
  FileText,
  Grid3X3,
  BarChart3,
  UserPlus,
  Settings,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Database,
  Send,
  Calendar,
  UserCheck,
  Lock,
  Image,
  Sparkles,
  LayoutDashboard,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";

type SidebarItem = {
  title: string;
  url: string;
  icon: typeof Users;
  ownerOnly?: boolean;
};

type SidebarGroupDef = {
  title: string;
  icon: typeof Users;
  ownerOnly?: boolean;
  items: SidebarItem[];
};

const navigationItems: SidebarGroupDef[] = [
  {
    title: "友達管理",
    icon: Users,
    items: [
      { title: "友達リスト", url: "/users/friends/list", icon: Users },
      { title: "友達追加URL", url: "/users/friends/add-url", icon: UserPlus, ownerOnly: true },
      { title: "ページデザイン", url: "/users/settings/friend-add-page", icon: Image, ownerOnly: true },
      { title: "タグ", url: "/users/friends/tags", icon: Tags, ownerOnly: true },
      { title: "友達情報管理", url: "/users/friends/custom-fields", icon: Database, ownerOnly: true },
    ],
  },
  {
    title: "配信設定",
    icon: MessageSquare,
    items: [
      { title: "一括配信", url: "/users/delivery/broadcast", icon: Send },
      { title: "シナリオ配信", url: "/users/delivery/scenario", icon: Calendar },
      // { title: "AIシナリオ生成", url: "/users/delivery/ai-scenario", icon: Sparkles },
      { title: "アンケート", url: "/users/delivery/surveys", icon: FileText },
      { title: "リッチメニュー", url: "/users/delivery/rich-menu", icon: Grid3X3 },
      { title: "クリエイティブ一覧", url: "/users/delivery/creatives", icon: FileText },
    ],
  },
  {
    title: "レポート",
    icon: BarChart3,
    items: [
      { title: "友達追加", url: "/users/reports/friends", icon: UserPlus },
      { title: "アンケート", url: "/users/reports/surveys", icon: FileText },
      { title: "一括配信", url: "/users/reports/broadcast-messages", icon: Send },
      { title: "シナリオ配信", url: "/users/reports/scenario-messages", icon: Calendar },
      { title: "リッチメニュー", url: "/users/reports/rich-menu", icon: Grid3X3 },
      { title: "CVダウンロード", url: "/users/reports/conversions", icon: FileText },
    ],
  },
  {
    title: "設定",
    icon: Settings,
    ownerOnly: true,
    items: [
      { title: "基本設定", url: "/users/settings", icon: Settings },
      { title: "自動返信設定", url: "/users/settings/auto-reply", icon: MessageSquare },
      { title: "権限管理", url: "/users/settings/permissions", icon: Lock },
      { title: "テスト配信ユーザー", url: "/users/settings/test-users", icon: UserCheck },
      { title: "クリック計測", url: "/users/settings/click-tracking", icon: Settings },
      { title: "CV計測", url: "/users/settings/cv-tracking", icon: Settings },
      { title: "ポイント減算", url: "/users/settings/point-decay", icon: Settings },
      { title: "プラン", url: "/users/settings/plan", icon: Settings },
      { title: "請求履歴", url: "/users/settings/billing", icon: Settings },
      { title: "利用者情報", url: "/users/settings/user", icon: Settings },
      { title: "利用マニュアル", url: "/users/settings/manual", icon: HelpCircle },
      { title: "問い合わせ", url: "/users/settings/contact", icon: HelpCircle },
    ],
  },
  {
    title: "外部連携",
    icon: Database,
    ownerOnly: true,
    items: [
      { title: "SiteLead連携", url: "/integrations/sitelead", icon: Database },
      { title: "アフィリエイト連携", url: "/integrations/affiliate", icon: Database },
    ],
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const SIDEBAR_STORAGE_KEY = "sidebar-expanded-groups";

export function AppSidebar() {
  const { state } = useSidebar();
  const page = usePage<{ user?: { role?: "owner" | "member" }; hasUnreadFriendMessages?: boolean }>();
  // 友だちからの未確認メッセージがあれば「友達リスト」に赤丸（#443）
  const hasUnreadFriendMessages = !!page.props.hasUnreadFriendMessages;
  const url = page.url;
  const currentPath = url;
  const collapsed = state === "collapsed";

  // メンバーには owner 限定のグループ・項目を非表示にする
  const isOwner = page.props.user?.role !== "member";
  const visibleNavigationItems = navigationItems
    .filter((group) => isOwner || !group.ownerOnly)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isOwner || !item.ownerOnly),
    }))
    .filter((group) => group.items.length > 0);

  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    // localStorageから開閉状態を復元
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Failed to load sidebar state from localStorage:", error);
    }

    // localStorageにデータがない場合は、全てのグループを開く
    return visibleNavigationItems.map((group) => group.title);
  });

  // expandedGroupsが変更されたらlocalStorageに保存
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(expandedGroups));
    } catch (error) {
      console.error("Failed to save sidebar state to localStorage:", error);
    }
  }, [expandedGroups]);

  const toggleGroup = (groupTitle: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupTitle)
        ? prev.filter((title) => title !== groupTitle)
        : [...prev, groupTitle]
    );
  };

  const isGroupExpanded = (groupTitle: string) => expandedGroups.includes(groupTitle);
  const isActive = (path: string) => currentPath === path;

  const getNavClass = (isActive: boolean) => {
    return classNames(
      "group flex gap-x-3 rounded-md p-2 text-sm font-semibold transition-colors",
      isActive
        ? "bg-primary/10 text-primary"
        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
    );
  };

  return (
    <Sidebar className="border-r border-border bg-sidebar" collapsible="icon">
      <SidebarContent className="gap-0">
        {/* Logo */}
        <div className="flex h-16 shrink-0 items-center border-b border-border px-6">
          <Link href="/users">
            <div className="text-xl font-bold text-primary">Nurturelead</div>
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* ダッシュボード（グループなしのトップレベル項目） */}
          <div className="mb-4">
            <Link href="/users/dashboard" className={getNavClass(isActive("/users/dashboard"))}>
              <LayoutDashboard className="h-4 w-4 shrink-0" />
              {!collapsed && <span>ダッシュボード</span>}
            </Link>
          </div>

          {visibleNavigationItems.map((group) => {
            const isExpanded = isGroupExpanded(group.title);
            const hasActiveItem = group.items.some((item) => isActive(item.url));

            return (
              <SidebarGroup key={group.title} className="mb-4">
                <SidebarGroupLabel
                  onClick={() => toggleGroup(group.title)}
                  className={classNames(
                    "flex cursor-pointer items-center justify-between text-xs font-semibold uppercase tracking-wider transition-colors",
                    hasActiveItem ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <group.icon className="h-4 w-4" />
                    {!collapsed && <span>{group.title}</span>}
                  </div>
                  {!collapsed && (
                    <div className="ml-auto">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </SidebarGroupLabel>

                {(isExpanded || collapsed) && (
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild={(item as any).isLogout ? false : true}>
                            {(item as any).isLogout ? (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  router.delete(item.url);
                                }}
                                className={getNavClass(isActive(item.url))}
                              >
                                <item.icon className="h-4 w-4 shrink-0" />
                                {!collapsed && <span>{item.title}</span>}
                              </button>
                            ) : (
                              <Link href={item.url} className={getNavClass(isActive(item.url))}>
                                <span className="relative shrink-0">
                                  <item.icon className="h-4 w-4 shrink-0" />
                                  {item.url === "/users/friends/list" && hasUnreadFriendMessages && (
                                    <span
                                      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500"
                                      aria-label="未確認のメッセージがあります"
                                    />
                                  )}
                                </span>
                                {!collapsed && <span>{item.title}</span>}
                              </Link>
                            )}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                )}
              </SidebarGroup>
            );
          })}
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
